import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { X, Mail, User } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND_LOGO_SRC } from "@/components/BrandLogo";

function DiscordIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0c-.164-.39-.405-.875-.617-1.25a.077.077 0 0 0-.074-.035A19.74 19.74 0 0 0 3.687 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.1.246.198.373.292a.077.077 0 0 1-.006.128 12.298 12.298 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.028Z" />
    </svg>
  );
}

function GoogleIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.5 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.7 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2C40.9 35.1 44 30 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function SoonBadge() {
  return (
    <span className="ml-auto rounded-md border border-[#3d4a1a] bg-[#3d4a1a]/40 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-300">
      Coming Soon
    </span>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [emailMode, setEmailMode] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const marketingBackHref = "/";

  useEffect(() => {
    // Prevent horizontal scrollbar on the login page
    const prevBg = document.body.style.background;
    document.body.style.background = "#000";
    return () => { document.body.style.background = prevBg; };
  }, []);

  async function loginDiscord() {
    try {
      const res = await api.get("/auth/discord/login", { params: { frontend_origin: window.location.origin } });
      if (res.data?.url) window.location.assign(res.data.url);
    } catch (_e) { /* noop */ }
  }

  async function submitEmail(e) {
    e?.preventDefault?.();
    if (!email || !password) return;
    if (authMode === "signup" && !username.trim()) {
      setError("Username is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (authMode === "signup") {
        await api.post("/auth/register", { email, password, username: username.trim() });
      } else {
        await api.post("/auth/login", { email, password });
      }
      // Post-login → /home on the app subdomain
      navigate("/home", { replace: true });
      // Fallback in case navigate is intercepted; full reload guarantees auth context refresh
      setTimeout(() => { window.location.assign("/home"); }, 50);
    } catch (err) {
      setError(err?.response?.data?.detail || (authMode === "signup" ? "Sign up failed." : "Invalid email or password."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen w-full overflow-hidden bg-black text-white"
    >
      {/* Ambient background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#c8ff00]/[0.04] blur-[130px]" />
        <div className="absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-white/[0.02] blur-[100px]" />
        <div className="absolute -right-32 bottom-1/4 h-[420px] w-[420px] rounded-full bg-white/[0.02] blur-[100px]" />
        {/* subtle noise/vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0),rgba(0,0,0,0.6))]" />
      </div>

      {/* Back to marketing */}
      <Link
        to="/"
        data-testid="login-back-to-home"
        className="absolute left-5 top-5 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[11.5px] font-semibold text-zinc-400 backdrop-blur hover:bg-white/[0.06] hover:text-white"
      >
        <X size={12} /> Close
      </Link>

      {/* Centered card */}
      <div className="relative flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Small logo above card */}
          <div className="mb-5 flex justify-center">
            <img
              data-testid="login-logo"
              src={BRAND_LOGO_SRC}
              alt="ClippingBase"
              className="h-16 w-16 rounded-2xl border border-white/10 object-contain shadow-[0_10px_40px_rgba(200,255,0,0.15)]"
            />
          </div>

          <div
            data-testid="login-card"
            className="rounded-3xl border border-white/10 bg-[#0a0a0c]/95 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8"
          >
            <h2 className="cb-display text-3xl text-white sm:text-4xl">
              Welcome, <span className="text-[#c8ff00]">back</span>
            </h2>

            {!emailMode ? (
              <>
                <div className="mt-7 space-y-3">
                  <button
                    onClick={loginDiscord}
                    data-testid="login-discord"
                    className="cb-btn-hover flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#1c1c20] px-4 py-3.5 text-left text-base font-bold text-white hover:bg-[#22222a]"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5865F2] text-white">
                      <DiscordIcon size={20} />
                    </span>
                    Continue with Discord
                  </button>

                  <button
                    onClick={() => setEmailMode(true)}
                    data-testid="login-email"
                    className="cb-btn-hover flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#1c1c20] px-4 py-3.5 text-left text-base font-bold text-white hover:bg-[#22222a]"
                  >
                    <span className="flex h-9 w-9 items-center justify-center text-zinc-200">
                      <Mail size={22} />
                    </span>
                    Continue with Email
                  </button>

                  <button
                    disabled
                    data-testid="login-google"
                    className="flex w-full cursor-not-allowed items-center gap-4 rounded-2xl border border-white/10 bg-[#1c1c20] px-4 py-3.5 text-left text-base font-bold text-zinc-400"
                  >
                    <span className="flex h-9 w-9 items-center justify-center">
                      <GoogleIcon size={22} />
                    </span>
                    Continue with Google
                    <SoonBadge />
                  </button>
                </div>

                <div className="my-7 flex items-center gap-3">
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Or</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>

                <div className="mb-3 text-sm font-bold text-white">
                  Are you a <span className="text-[#c8ff00]">Client?</span>
                </div>
                <button
                  disabled
                  data-testid="login-client"
                  className="flex w-full cursor-not-allowed items-center gap-4 rounded-2xl border border-white/10 bg-[#1c1c20] px-4 py-3.5 text-left text-base font-bold text-zinc-400"
                >
                  <span className="flex h-9 w-9 items-center justify-center text-zinc-300">
                    <User size={22} />
                  </span>
                  Sign In as a Client
                  <SoonBadge />
                </button>
              </>
            ) : (
              <form onSubmit={submitEmail} className="mt-6 space-y-3" data-testid="login-email-form">
                <div className="mb-2 inline-flex w-full overflow-hidden rounded-xl border border-white/10 bg-[#0d0d10] p-1 text-xs">
                  <button
                    type="button"
                    data-testid="login-mode-login"
                    onClick={() => { setAuthMode("login"); setError(""); }}
                    className={`flex-1 rounded-lg px-3 py-2 font-bold transition-colors ${authMode === "login" ? "bg-[#c8ff00] text-black" : "text-zinc-300 hover:text-white"}`}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    data-testid="login-mode-signup"
                    onClick={() => { setAuthMode("signup"); setError(""); }}
                    className={`flex-1 rounded-lg px-3 py-2 font-bold transition-colors ${authMode === "signup" ? "bg-[#c8ff00] text-black" : "text-zinc-300 hover:text-white"}`}
                  >
                    Create account
                  </button>
                </div>

                {authMode === "signup" && (
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] text-zinc-500">Username</span>
                    <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      data-testid="login-username-input"
                      className="w-full rounded-xl border border-white/10 bg-[#0a0a0c] px-3.5 py-3 text-sm text-white outline-none transition-colors focus:border-[#c8ff00]/60"
                      placeholder="yourname"
                    />
                  </label>
                )}

                <label className="block">
                  <span className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] text-zinc-500">Email</span>
                  <input
                    type="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="w-full rounded-xl border border-white/10 bg-[#0a0a0c] px-3.5 py-3 text-sm text-white outline-none transition-colors focus:border-[#c8ff00]/60"
                    placeholder="you@example.com"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] text-zinc-500">Password</span>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="w-full rounded-xl border border-white/10 bg-[#0a0a0c] px-3.5 py-3 text-sm text-white outline-none transition-colors focus:border-[#c8ff00]/60"
                    placeholder="••••••••"
                  />
                </label>

                {authMode === "signup" && (
                  <div data-testid="login-password-warning" className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5 text-[11px] leading-relaxed text-amber-200">
                    ⚠ Passwords aren&apos;t retrievable if forgotten — you may lose your account. Save your password somewhere safe.
                  </div>
                )}

                {error && <p data-testid="login-error" className="text-xs text-rose-400">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="login-email-submit"
                  className="cb-btn-hover mt-2 w-full rounded-xl bg-[#c8ff00] px-4 py-3 text-sm font-bold text-black hover:bg-[#b8ef00] disabled:opacity-60"
                >
                  {submitting ? (authMode === "signup" ? "Creating…" : "Signing in…") : (authMode === "signup" ? "Create account" : "Sign In")}
                </button>
                <button
                  type="button"
                  onClick={() => { setEmailMode(false); setError(""); setAuthMode("login"); }}
                  data-testid="login-back"
                  className="mt-1 w-full text-center text-xs text-zinc-400 hover:text-white"
                >
                  ← Back to all sign-in methods
                </button>
              </form>
            )}
          </div>

          <div className="mt-5 text-center text-[11.5px] text-zinc-500">
            By continuing you agree to the{" "}
            <a href="/terms" className="text-zinc-300 underline underline-offset-2 hover:text-white">Terms</a>
            {" "}and{" "}
            <a href="/privacy" className="text-zinc-300 underline underline-offset-2 hover:text-white">Privacy Policy</a>.
          </div>
        </div>
      </div>
    </div>
  );
}
